import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI } from '../../services/api';

function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }

    analyticsAPI.getStats()
      .then(data => {
        setStats(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [user, navigate]);

  if (!user?.isAdmin) {
    return null;
  }

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (error) {
    return (
      <div className="analytics">
        <div className="analytics-header">
          <h1>Analytics Dashboard</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
        <div className="error-message">Failed to load analytics: {error}</div>
      </div>
    );
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <h1>Analytics Dashboard</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>

      <div className="stats-grid">
        {/* Users Card */}
        <div className="stat-card">
          <h3>Users</h3>
          <div className="stat-row">
            <div className="stat-item">
              <div className="stat-number">{stats.users.total}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.users.activeThisWeek}</div>
              <div className="stat-label">Active This Week</div>
            </div>
          </div>
        </div>

        {/* Hardest Parts Card */}
        <div className="stat-card">
          <h3>Average Scores by Part</h3>
          {stats.scoresByPart.length > 0 ? (
            <div className="scores-list">
              {stats.scoresByPart
                .sort((a, b) => a.avgScore - b.avgScore)
                .map(part => (
                  <div key={part.part} className="score-row">
                    <span className="part-label">Part {part.part}</span>
                    <div className="score-bar-container">
                      <div
                        className="score-bar-fill"
                        style={{ width: `${part.avgScore * 100}%` }}
                      />
                    </div>
                    <span className="score-value">{Math.round(part.avgScore * 100)}%</span>
                    <span className="completions-count">({part.completions})</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="no-data">No completion data yet</p>
          )}
        </div>

        {/* Missed Questions Card */}
        <div className="stat-card stat-card-wide">
          <h3>Most Missed Questions</h3>
          {stats.missedQuestions.length > 0 ? (
            <table className="missed-questions-table">
              <thead>
                <tr>
                  <th>Question ID</th>
                  <th>Part</th>
                  <th>Times Missed</th>
                </tr>
              </thead>
              <tbody>
                {stats.missedQuestions.map(q => (
                  <tr key={q.questionId}>
                    <td>{q.questionId}</td>
                    <td>Part {q.partNumber}</td>
                    <td>{q.timesMissed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-data">No missed questions data yet</p>
          )}
        </div>

        {/* Completion Rates Card */}
        <div className="stat-card">
          <h3>Completion Rates by Part</h3>
          {stats.completionRates.length > 0 ? (
            <div className="completion-rates">
              {stats.completionRates.map(part => (
                <div key={part.part} className="completion-row">
                  <span className="part-label">Part {part.part}</span>
                  <div className="progress-bar-horizontal">
                    <div
                      className="progress-bar-fill-horizontal"
                      style={{ width: `${part.rate * 100}%` }}
                    />
                  </div>
                  <span className="rate-value">{Math.round(part.rate * 100)}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No completion data yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Analytics;
